import { DashboardModal } from "@dashboard/components/Modal";
import { Box, Button, Checkbox, Input, Text, Textarea } from "@saleor/macaw-ui-next";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

import { batchMessages } from "./batchMessages";

export interface BatchFormValues {
  // `datetime-local` value ("YYYY-MM-DDTHH:mm") in the browser's time zone.
  arrivalAt: string;
  // The arrival is only an ETA — labels are often printed before the goods
  // arrive; unticked (with the real time) once they do.
  isEstimated: boolean;
  supplier: string;
  reference: string;
  quantity: string;
  notes: string;
}

interface BatchFormDialogProps {
  open: boolean;
  // What the batch is for (create: the variant; edit: the existing code).
  subtitle: string;
  // Edit mode can correct the arrival time (labels are often printed before
  // the goods arrive, with an estimate) but never the code — printed labels
  // already carry it.
  mode: "create" | "edit";
  initialValues: BatchFormValues;
  loading: boolean;
  error?: string;
  onClose: () => void;
  onSubmit: (values: BatchFormValues) => void;
}

export const toDateTimeLocalValue = (date: Date) => {
  const pad = (value: number) => String(value).padStart(2, "0");

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
};

export const BatchFormDialog = ({
  open,
  subtitle,
  mode,
  initialValues,
  loading,
  error,
  onClose,
  onSubmit,
}: BatchFormDialogProps) => {
  const intl = useIntl();
  const [values, setValues] = useState(initialValues);

  useEffect(() => {
    if (open) {
      setValues(initialValues);
    }
    // Reset only when (re)opened, not on every keystroke's new initialValues.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const patch = (change: Partial<BatchFormValues>) =>
    setValues(previous => ({ ...previous, ...change }));

  return (
    <DashboardModal onChange={onClose} open={open}>
      <DashboardModal.Content size="sm">
        <DashboardModal.Header>
          {intl.formatMessage(
            mode === "create" ? batchMessages.registerTitle : batchMessages.editTitle,
          )}
        </DashboardModal.Header>
        <Box display="flex" flexDirection="column" gap={3}>
          <Text size={2} color="default2">
            {subtitle}
          </Text>
          <Input
            type="datetime-local"
            size="small"
            label={intl.formatMessage(batchMessages.arrivalAt)}
            value={values.arrivalAt}
            onChange={event => patch({ arrivalAt: event.target.value })}
          />
          <Checkbox
            checked={values.isEstimated}
            onCheckedChange={checked => patch({ isEstimated: checked === true })}
          >
            <Text size={2}>{intl.formatMessage(batchMessages.isEstimated)}</Text>
          </Checkbox>
          {mode === "edit" && (
            <Text size={1} color="default2">
              {intl.formatMessage(batchMessages.arrivalEditHint)}
            </Text>
          )}
          <Input
            size="small"
            label={intl.formatMessage(batchMessages.supplier)}
            value={values.supplier}
            onChange={event => patch({ supplier: event.target.value })}
          />
          <Input
            size="small"
            label={intl.formatMessage(batchMessages.reference)}
            value={values.reference}
            onChange={event => patch({ reference: event.target.value })}
          />
          <Text size={1} color="default2">
            {intl.formatMessage(batchMessages.referenceHint)}
          </Text>
          <Input
            type="number"
            min={0}
            size="small"
            label={intl.formatMessage(batchMessages.quantity)}
            value={values.quantity}
            onChange={event => patch({ quantity: event.target.value })}
          />
          <Textarea
            size="small"
            rows={2}
            label={intl.formatMessage(batchMessages.notes)}
            value={values.notes}
            onChange={event => patch({ notes: event.target.value })}
          />
          {error && (
            <Text size={2} color="critical1">
              {error}
            </Text>
          )}
        </Box>
        <DashboardModal.Actions>
          <Button variant="secondary" onClick={onClose}>
            {intl.formatMessage(batchMessages.cancel)}
          </Button>
          <Button
            variant="primary"
            disabled={loading || !values.arrivalAt}
            onClick={() => onSubmit(values)}
          >
            {intl.formatMessage(mode === "create" ? batchMessages.register : batchMessages.save)}
          </Button>
        </DashboardModal.Actions>
      </DashboardModal.Content>
    </DashboardModal>
  );
};
